'use client'

import { useState } from 'react'
import { isNumber } from 'lodash'
import { Button, Icon, Image, Loader } from '@vinted/web-ui'
import { RotateRight16, X16 } from '@vinted/monochrome-icons'

import {
  ImageOrientation,
  MediaSelectPhotoOrientation,
  MediaUploadPhotoModel,
} from '@marketplace-web/item-upload/media-upload-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { getPhotoThumbnail } from '../../utils'

import Sortable from '../SortableList'
import useMediaSelectContext from '../../hooks/useMediaSelectContext'

enum ItemThumbnailSize {
  XLarge = 'thumb364x428',
}

type Props = {
  photo: MediaUploadPhotoModel
  orientation: ImageOrientation
  onDelete: (id: number) => void
  index: number
  imageAlt: string
  isControlsHidden: boolean
  enableRotation: boolean
  setPhotoOrientation: (photoOrientation: MediaSelectPhotoOrientation) => void
}

const PhotoBox = ({
  photo,
  orientation,
  onDelete,
  index,
  imageAlt,
  isControlsHidden,
  enableRotation,
  setPhotoOrientation,
}: Props) => {
  const [rotation, setRotation] = useState(0)
  const { photoInitializationState, updatePhotoInitializationState } = useMediaSelectContext()

  const isLoading = !photoInitializationState[photo.id]
  const translate = useTranslate('item_upload.media_select')

  const handleRotateClockwise = (id: number) => () => {
    let newRotation: number
    let newOrientation = orientation

    if (!isNumber(orientation)) return

    if (rotation === 270) {
      newRotation = 0
    } else {
      newRotation = rotation + 90
    }

    if (orientation === ImageOrientation.Degree270) {
      newOrientation = ImageOrientation.Degree0
    } else {
      newOrientation += ImageOrientation.Degree90
    }

    setRotation(newRotation)
    setPhotoOrientation({ id, orientation: newOrientation })
  }

  const handleDelete = (id: number) => () => {
    onDelete(id)
  }

  const handleImageLoad = () => {
    updatePhotoInitializationState(photo.id, true)
  }

  const renderControls = () => {
    if (isControlsHidden || isLoading) return null

    return (
      <>
        <div className="photo-box__delete-button">
          <Button
            testId={`media-select-grid-delete-button-${index}`}
            icon={<Icon name={X16} color={Icon.Color.GreyscaleLevel1} testId="x" />}
            size={Button.Size.Small}
            inverse
            styling={Button.Styling.Filled}
            inline
            onClick={handleDelete(photo.id)}
            aria={{
              'aria-label': translate('a11y.delete_photo'),
            }}
          />
        </div>
        {enableRotation && (
          <div className="photo-box__action-button">
            <Button
              testId={`media-select-grid-rotate-button-${index}`}
              icon={
                <Icon
                  name={RotateRight16}
                  color={Icon.Color.GreyscaleLevel1}
                  testId="rotate-right"
                />
              }
              size={Button.Size.Small}
              inverse
              styling={Button.Styling.Filled}
              inline
              onClick={handleRotateClockwise(photo.id)}
              aria={{
                'aria-label': translate('a11y.rotate_photo'),
              }}
            />
          </div>
        )}
      </>
    )
  }

  return (
    <div className="photo-box">
      <Sortable.Item id={photo.id} index={index}>
        <div className="photo-box__image-container" role="button" tabIndex={0}>
          <div
            data-testid={`image-wrapper-${index}`}
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            <Image
              src={getPhotoThumbnail(photo, ItemThumbnailSize.XLarge) || photo.fullSizeUrl}
              scaling={Image.Scaling.Cover}
              ratio={Image.Ratio.Square}
              styling={Image.Styling.Rounded}
              onLoad={handleImageLoad}
              alt={imageAlt}
            />
          </div>
        </div>
      </Sortable.Item>
      {renderControls()}
      {isLoading && (
        <div data-testid={`media-select-grid-item-${index}-loader`} className="photo-box__loader">
          <Loader size={Loader.Size.XLarge} />
        </div>
      )}
    </div>
  )
}

export default PhotoBox
